import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SectionHeader } from "@/components/SectionHeader";
import { PLAYER } from "@/data/portfolio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(`Message sent. ${PLAYER.firstName} will get back to you shortly.`);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Failed to send. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Failed to send.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#0a0a0a] text-white"
    >
      <SectionHeader
        eyebrow="04 / Contact"
        title="Let's Talk."
        description="For scouting enquiries, club opportunities, brand partnerships or media — drop a line below."
        testId="contact-header"
      />

      <div className="mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left: contact info */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <ul className="space-y-8">
            <li className="flex items-start gap-4" data-testid="contact-info-email">
              <Mail className="text-[#e63946] mt-1" size={20} />
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-neutral-400">
                  Email
                </div>
                <a
                  href={`mailto:${PLAYER.contact.email}`}
                  className="mt-1 block font-display text-xl md:text-2xl font-semibold tracking-tight hover:text-[#e63946] transition-colors"
                >
                  {PLAYER.contact.email}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4" data-testid="contact-info-phone">
              <Phone className="text-[#e63946] mt-1" size={20} />
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-neutral-400">
                  Phone
                </div>
                <a
                  href={`tel:${PLAYER.contact.phone.replace(/\s/g, "")}`}
                  className="mt-1 block font-display text-xl md:text-2xl font-semibold tracking-tight hover:text-[#e63946] transition-colors"
                >
                  {PLAYER.contact.phone}
                  <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-[0.25em] text-[#e63946] bg-[#e63946]/10 px-2 py-0.5 rounded-sm">
                    Primary
                  </span>
                </a>
                {PLAYER.contact.phoneSecondary && (
                  <a
                    href={`tel:${PLAYER.contact.phoneSecondary.replace(/\s/g, "")}`}
                    data-testid="contact-info-phone-secondary"
                    className="mt-2 block font-display text-lg md:text-xl font-medium tracking-tight text-neutral-300 hover:text-[#e63946] transition-colors"
                  >
                    {PLAYER.contact.phoneSecondary}
                    <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-500">
                      Alt
                    </span>
                  </a>
                )}
              </div>
            </li>
            <li className="flex items-start gap-4" data-testid="contact-info-location">
              <MapPin className="text-[#e63946] mt-1" size={20} />
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-neutral-400">
                  Based in
                </div>
                <div className="mt-1 font-display text-xl md:text-2xl font-semibold tracking-tight">
                  {PLAYER.contact.location}
                </div>
              </div>
            </li>
          </ul>

          <div className="mt-12 pt-12 border-t border-white/10">
            <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-neutral-400 mb-5">
              Socials
            </div>
            <div className="flex flex-wrap gap-3">
              {PLAYER.contact.socials.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`social-link-${s.label.toLowerCase()}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 text-xs font-semibold uppercase tracking-widest hover:bg-[#e63946] hover:border-[#e63946] transition-colors"
                >
                  {s.label}
                  <ArrowUpRight size={14} />
                </a>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Right: form */}
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={onSubmit}
          className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5"
          data-testid="contact-form"
        >
          <div className="md:col-span-1">
            <Label htmlFor="name" className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-400">
              Name
            </Label>
            <Input
              id="name"
              name="name"
              value={form.name}
              onChange={onChange}
              data-testid="contact-input-name"
              placeholder="Your full name"
              className="mt-2 rounded-none bg-transparent border-x-0 border-t-0 border-b border-white/30 px-0 text-white placeholder:text-neutral-600 focus-visible:ring-0 focus-visible:border-[#e63946] h-12"
            />
          </div>
          <div className="md:col-span-1">
            <Label htmlFor="email" className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-400">
              Email
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              data-testid="contact-input-email"
              placeholder="you@club.com"
              className="mt-2 rounded-none bg-transparent border-x-0 border-t-0 border-b border-white/30 px-0 text-white placeholder:text-neutral-600 focus-visible:ring-0 focus-visible:border-[#e63946] h-12"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="subject" className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-400">
              Subject
            </Label>
            <Input
              id="subject"
              name="subject"
              value={form.subject}
              onChange={onChange}
              data-testid="contact-input-subject"
              placeholder="Scouting / Partnership / Media"
              className="mt-2 rounded-none bg-transparent border-x-0 border-t-0 border-b border-white/30 px-0 text-white placeholder:text-neutral-600 focus-visible:ring-0 focus-visible:border-[#e63946] h-12"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="message" className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-400">
              Message
            </Label>
            <Textarea
              id="message"
              name="message"
              value={form.message}
              onChange={onChange}
              rows={5}
              data-testid="contact-input-message"
              placeholder="Tell us a little about your enquiry..."
              className="mt-2 rounded-none bg-transparent border-x-0 border-t-0 border-b border-white/30 px-0 text-white placeholder:text-neutral-600 focus-visible:ring-0 focus-visible:border-[#e63946] resize-none"
            />
          </div>
          <div className="md:col-span-2 mt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#e63946] text-white text-xs font-semibold uppercase tracking-widest hover:bg-white hover:text-[#0a0a0a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  Send Message
                  <ArrowUpRight size={16} />
                </>
              )}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
